<template>
  <div class="my-3">
    <h1 class="text-center">
      <strong>Terms of Service</strong>
    </h1>
    <p>
      This Terms of Service (the “<strong>TOS</strong>”) is a legally binding agreement and is agreed to between RJW LLC, d/b/a
      {{ platform }} (“<strong>{{ platform }}</strong
      >”, “<strong>us</strong>”, “<strong>we</strong>” or “<strong>our</strong>”) and you, or, if you represent an entity or other organization, that
      entity or organization (in either case“<strong>you</strong>” or “<strong>your</strong>”).
    </p>

    <p>
      {{ platform }} offers a technology platform that provides an online platform (the “<strong>Platform</strong>”) in which consumers and shops can
      list and sell their {{ industry }} and other associated items (“<strong>Items</strong>”), and buyers can browse and purchase Items. You may
      access the Platform through the website located at
      <a :href="`http://www.${platformDomain}.com/`"> {{ `http://www.${platformDomain}.com/` }} </a> and through various other websites operated by
      {{ platform }} (each, a “<strong>Site</strong>”) (each Site is treated as a part of the “Platform” for purposes of this TOS). The Platform
      provides users the ability to purchase Items ("<strong>Buyers</strong>") and users to list and sell Items, either through {{ platform }}’s
      marketplace or through a third-party clearing house ("<strong>Sellers,</strong>" collectively with Buyers, “<strong>Members</strong>”); and,
      other wholesale and inventory management services, including, but not limited to, electronic signature services, through the Platform
      (collectively, the “<strong>Services</strong>”). A Member may be both a Buyer and Seller.
    </p>

    <p>
      PLEASE READ THE FOLLOWING TOS CAREFULLY. BY ACCESSING OR USING OUR SITES, PLATFORM, AND SERVICES, YOU HEREBY AGREE TO BE BOUND BY THE TERMS AND
      ALL TERMS INCORPORATED HEREIN BY REFERENCE INCLUDING THE PRIVACY POLICY LOCATED AT
      <a :href="`http://${platformDomain}.com/privacy/`"> {{ `http://${platformDomain}.com/privacy/` }} </a>. IT IS THE RESPONSIBILITY OF YOU TO READ
      THE TERMS AND CONDITIONS BEFORE PROCEEDING TO USE THIS SITE, THE PLATFORM, AND SERVICES. IF YOU DO NOT EXPRESSLY AGREE TO ALL OF THE TERMS AND
      CONDITIONS, THEN YOU MAY NOT ACCESS OR USE OUR SITES, OUR PLATFORM, OR SERVICES. THIS TERMS OF SERVICE AGREEMENT IS EFFECTIVE AS OF01/06/2020.
    </p>

    <p>YOU SHOULD CAREFULLY READ THIS TOS, INCLUDING THE REQUIRED USE OF ARBITRATION, AS FULLY DESCRIBED IN THE “ARBITRATION” SECTION OF THIS TOS.</p>
    <ol>
      <li>
        <strong>DEFINITIONS</strong>
        <p>
          Terms used in this TOS have the definitions given in this TOS or, if not defined in this TOS, have their plain English meaning as commonly
          interpreted in the United States.
        </p>
      </li>
      <li>
        <strong>SERVICES</strong>
        <ol style="list-style-type: upper-latin">
          <li>
            <u> Modifications.</u> {{ platform }} shall reserve the right at any time it may deem fit, to modify, alter, revise, update, suspend or
            discontinue, whether temporarily or permanently, the Platform and Service, or any part thereof (“<strong>Modifications</strong>”), with or
            without prior notice. In addition, we shall not be held liable to you or to any third party for any such Modification, or any part
            thereof. Your continued use of the Services or Platform after any Modification shall constitute your acceptance of such Modification.
            Frequent review of this TOS and any and all applicable terms and policies should be made by you to ensure you are aware of all terms and
            policies currently in effect.
          </li>
          <li>
            <u>No Responsibility.</u>
            All purchases made through the Platform are considered to be made directly (and any contract for purchase and sale is) between the Buyer
            and Seller; {{ platform }} is not a traditional auctioneer, nor is it a Seller, Buyer, or carrier. The Platform includes pricing and
            listing assistance and shipping, but not buying or selling. {{ platform }} has no responsibility whatsoever in participating in any
            disputes between Buyers and Sellers, {{ platform }} further has no control over and does not guarantee i) the existence, quality, safety,
            authenticity, or legality of Items advertised on the Platform; ii) the truth or accuracy of any Seller’s content or listings of Items on
            the Platform; iii) the ability of Sellers to sell Items through the Platform and ship Items within required shipping windows; iv) the
            ability of Buyers to pay for Items purchased through the Platform; or v) that a Buyer or Seller will actually complete a transaction,
            effectuate trouble-free delivery and shipping, or return an Item through the Platform.
          </li>
        </ol>
      </li>
      <li>
        <strong>MEMBER ELIGIBILITY AND REGISTRATION</strong>
        <ol style="list-style-type: upper-latin">
          <li>
            <u>Member Eligibility.</u> Access to and use of the Platform and Services, are by membership only and are intended for use by individuals
            who are at least 21 years of age and able to form legally binding contracts. You represent and warrant that you are not: i) a citizen or
            resident of (or located in) any jurisdiction where use of the Platform, Services, or Items, are prohibited by law; ii)not an individual
            that has been previously barred from thePlatformor Services under the laws and statutes of the United States or other applicable
            jurisdiction; and iii) on the list of prohibited individuals which may be identified on any government export exclusion report (<a
              href="http://www.bis.doc.gov/complianceandenforcement/liststocheck.htm"
            >
              http://www.bis.doc.gov/complianceandenforcement/liststocheck.htm </a
            >) nor a member of any other government which may be part of an export-prohibited country identified in applicable export and import laws
            and regulations. {{ platform }} reserves the right to deny any applicant for any reason other than reasons based on race, gender, or
            religion, or any other legally protected category.
          </li>
          <li>
            <u>Notice to Members.</u> {{ platform }} has no obligation to screen or verify the capacity, suitability, or background of any Member. It
            is your responsibility to make your own assessment of other Members that you coordinate buying and selling of Items with or otherwise
            interact with through the Platform. You agree that {{ platform }} has no responsibility or liability to you related to any Services or
            Items provided through the Platform except as expressly set forth in this TOS. Members may be required to agree to terms and conditions
            applicable to the buying and selling of Items. {{ platform }} is not a party to and shall have no responsibility with regard to any
            agreement that any Member enters into with any other Member or any other third party. {{ platform }} will not participate in disputes
            between Members. Your use of the Services, Platform or Items available through the Platform is at your own risk. {{ platform }} shall not
            have any liability in any way related to transactions between Members.
          </li>
          <li>
            <u>Registration.</u> Access to and use of certain Services will require that you establish a Member account (a “<strong
              >Member Account</strong
            >”)when you register, {{ platform }} may collect information such as your name, e-mail address, birth date, gender, mailing address,
            occupation, industry and personal interests. You can edit your Member Account information at any time. Once you register with
            {{ platform }} and sign in to thePlatform, you are no longer anonymous to {{ platform }}. Furthermore, you hereby acknowledge, understand
            and agree to: i) furnish factual, correct, current and complete information with regards to you, as may be requested by the data
            registration process; and ii) maintain and promptly update your registration and profile information in an effort to maintain accuracy and
            completeness at all times. If you knowingly provide any information of a false, untrue, inaccurate or incomplete nature,
            {{ platform }} will have sufficient grounds and rights to suspend or terminate your Member Account, and as such refuse any and all current
            or future use of thePlatformor Services, or any portion thereof.
          </li>
        </ol>
      </li>
      <li>
        <strong>MEMBER ACCOUNT, USERNAME, PASSWORD AND SECURITY</strong>
        <p>
          When you set up a Member Account, you are the sole authorized user of your Member Account. You shall be responsible for maintaining the
          secrecy and confidentiality of your password and for all activities that transpire on or within your Member Account. It is your
          responsibility for any act or omission of any user(s) that access your Member Account information that, if undertaken by you, would be
          deemed a violation of the TOS. It shall be your responsibility to notify {{ platform }} immediately if you notice any unauthorized access or
          use of your Member Account or password or any other breach of security. {{ platform }} shall not be held liable for any loss and/or damage
          arising from any failure to comply with this TOS.
        </p>
      </li>
      <li>
        <strong>FEES AND PAYMENT FOR PURCHASED SERVICES</strong>
        <p>
          {{ platform }} offers a range of subscription plans for the Services. Each plan includes an outline of the Services offered. You will select
          your plan as part of your registration for the Services. Each subscription period for a plan will be based on the subscription chosen and
          the preferred billing cycle. You may choose to be billed for the plan annually or in less than annual increments, but regardless of billing
          cycle, you are responsible for subscription fees for the entire subscription period chosen, and fees are non-refundable. If no billing cycle
          is preferred, the default billing cycle is monthly and the cycle starts the first of every month. As an express condition of your use of and
          access to the Services, you agree to pay all fees applicable to your subscription plan. If you downgrade your subscription plan, your new
          rate will be charged beginning in the next billing cycle.
          <span v-if="industry == 'hemp'">
            In addition to the subscription fees outlined above, if you sell any Items through the Services (either through the
            {{ platform }} marketplace or through the third party clearing house) you shall pay {{ platform }} as follows: (i) 2% of the sale price
            (exclusive of taxes) for each Item sold through the marketplace and (ii) 4% of the sale price (exclusive of taxes) for each Item sold
            through the third party clearing house (the fee in subsection (i) together with the fee in subsection (ii), the “Transaction Fees”). You
            shall pay the Transaction Fees to {{ platform }} when determined by {{ platform }} in its sole discretion, which will be either at the
            time of the sale or 30 days after the date of the applicable invoice for the Transaction Fee. If the Transaction Fee is due at the time of
            the sale, you hereby authorize {{ platform }} to subtract the applicable Transaction Fee from amount paid by the Buyer for that
            transaction. You acknowledge that the list price that you list for the Items sold through the Services <em>minus</em>the Transaction Fees
            is the full amount that we owe you for the sale of such Items and in no event will we owe you more even if such Items are sold for more
            than the list price by a third party broker.
          </span>
        </p>
      </li>
      <li>
        <strong>PAYMENTS SERVICE</strong>
        <p>
          You may be permitted to purchase Items through the Platform as a Buyer. {{ platform }} uses a payment processor to facilitate these
          transactions. Unless otherwise clearly stated, {{ platform }} payment processing services are provided by Stripe and are subject to the
          Stripe Connected Account Agreement, which includes the Stripe Terms of Service (collectively, the “<strong>Stripe Services Agreement</strong
          >”). By agreeing to this TOS or continuing to utilize {{ platform }} for payment processing services, you agree to be bound by the Stripe
          Services Agreement, as the same may be modified by Stripe from time to time. As a condition of {{ platform }} enabling payment processing
          services through Stripe, you agree to provide {{ platform }} accurate and complete information about you and your business, and you
          authorize {{ platform }} to share with Stripe such information and transaction information related to your use of the payment processing
          services provided by Stripe.
        </p>
      </li>
      <li>
        <strong>PRIVACY POLICY</strong>
        <p>
          Your registration data and various other personal information are protected by the {{ platform }} Privacy Policy (see the full Privacy
          Policy at <a :href="`http://${platformDomain}.com/privacy/`"> {{ `http://${platformDomain}.com/privacy/` }} </a>). As a Member, you herein
          consent to the collection and use of the information provided, including the transfer of information within the United States and/or other
          countries for storage, processing or use by {{ platform }} and/or our subsidiaries and affiliates.
        </p>
      </li>
      <li>
        <strong>CONDUCT</strong>
        <ol style="list-style-type: upper-latin">
          <li>
            <u>Content Posted.</u> You herein acknowledge, understand and agree that all information, text, software, data, photographs, music, video,
            messages, tags or any other content, whether it is publicly or privately posted and/or transmitted, is the expressed sole responsibility
            of the individual from whom the content originated. In short, this means that you are solely responsible for any and all content posted,
            uploaded, emailed, transmitted or otherwise made available by way of the Services, and as such, we do not guarantee the accuracy,
            integrity or quality of such content. It is expressly understood that by use of the Services, you may be exposed to content including, but
            not limited to, any errors or omissions in any content posted, and/or any loss or damage of any kind incurred as a result of the use of
            any content posted, emailed, transmitted or otherwise made available by {{ platform }}.
          </li>
          <li>
            <u>Prohibited Activities.</u> You herein agree not to make use of thePlatform orServices for the purpose of:
            <ol style="list-style-type: lower-latin">
              <li>
                uploading, posting, emailing, transmitting, or otherwise making available any content that shall be deemed unlawful, harmful,
                threatening, abusive, harassing, tortious, defamatory, vulgar, obscene, libelous, or invasive of another's privacy or which is
                hateful, and/or racially, ethnically, or otherwise objectionable;
              </li>
              <li>causing harm to minors in any manner whatsoever;</li>
              <li>
                impersonating any individual or entity, including, but not limited to, any {{ platform }} officials, forum leaders, guides or hosts or
                falsely stating or otherwise misrepresenting any affiliation with an individual or entity;
              </li>
              <li>
                forging captions, headings or titles or otherwise offering any content that you personally have no right to pursuant to any law nor
                having any contractual or fiduciary relationship with;
              </li>
              <li>
                uploading, posting, emailing, transmitting or otherwise offering any such content that may infringe upon any patent, copyright,
                trademark, or any other proprietary or intellectual rights of any other party;
              </li>
              <li>
                uploading, posting, emailing, transmitting or otherwise offering any content that you do not personally have any right to offer
                pursuant to any law or in accordance with any contractual or fiduciary relationship;
              </li>
              <li>
                uploading, posting, emailing, transmitting, or otherwise offering any unsolicited or unauthorized advertising, promotional flyers,
                "junk mail," "spam," or any other form of solicitation, except in any such areas that may have been designated for such purpose;
              </li>
              <li>
                uploading, posting, emailing, transmitting, or otherwise offering any source that may contain a software virus or other computer code,
                any files and/or programs which have been designed to interfere, destroy and/or limit the operation of any computer software,
                hardware, or telecommunication equipment;
              </li>
              <li>
                disrupting the normal flow of communication, or otherwise acting in any manner that would negatively affect other users' ability to
                participate in any real time interactions;
              </li>
              <li>
                interfering with or disrupting any Services, servers and/or networks that may be connected or related to our website, including, but
                not limited to, the use of any device software and/or routine to bypass the robot exclusion headers;
              </li>
              <li>
                intentionally or unintentionally violating any local, state, federal, national or international law, including, but not limited to,
                rules, guidelines, and/or regulations decreed by the U.S. Securities and Exchange Commission, in addition to any rules of any nation
                or other securities exchange, that would include without limitation, the New York Stock Exchange, the American Stock Exchange, or the
                NASDAQ, and any regulations having the force of law;
              </li>
              <li>
                providing informational support or resources, concealing and/or disguising the character, location, and or source to any organization
                delegated by the United States government as a "foreign terrorist organization" in accordance to Section 219 of the Immigration
                Nationality Act;
              </li>
              <li>"stalking" or with the intent to otherwise harass another individual; and/or</li>
              <li>
                collecting or storing of any personal data relating to any other Member or user in connection with the prohibited conduct and/or
                activities which have been set forth in the aforementioned paragraphs.
              </li>
            </ol>
          </li>
          <li>
            <u>Right to Remove Content.</u> {{ platform }} herein reserves the right to pre-screen, refuse and/or delete any content currently
            available through thePlatformor Services. In addition, {{ platform }} reserves the right to remove and/or delete any such content that
            would violate this TOS or which would otherwise be considered offensive to other visitors, users and/or Members.
          </li>

          <li>
            <u>Right to Disclose Content.</u> {{ platform }} herein reserves the right to access, preserve and disclose Member Account information and
            content if it is requested to do so by law or in good faith belief that any such action is deemed reasonably necessary for: i) compliance
            with any legal process; ii) enforcement of the TOS; iii) responding to any claim that therein contained content is in violation of the
            rights of any third party; iv) responding to requests for customer service; or v) protecting the rights, property or the personal safety
            of {{ platform }}, its visitors, users and Members, including the general public.
          </li>

          <li>
            <u>Security of Content.</u> {{ platform }} herein reserves the right to include the use of security components that may permit digital
            information or material to be protected, and that such use of information and material is subject to usage guidelines and regulations
            established by {{ platform }} or any other content providers supplying content services to {{ platform }}. You are hereby prohibited from
            making any attempt to override or circumvent any of the embedded usage rules on thePlatformor in the Services. Unauthorized reproduction,
            publication, distribution, or exhibition of any information or materials supplied by the Services, despite whether done so in whole or in
            part, is expressly prohibited.
          </li>
        </ol>
      </li>
      <li>
        <strong>MARKETPLACE RISK</strong>
        <ol style="list-style-type: upper-latin">
          <li>
            <u>Marketplace Only.</u> {{ platform }} is not affiliated with or endorsed by any shop, manufacturer, retailer or brand of the Items made
            available through the Platform: {{ platform }} is a marketplace and technology platform and is not involved in any transaction between
            Buyers and Sellers, does not obtain title to any purchased Items and does not act as a Buyer, Seller (or affiliate for a Buyer or Seller)
            or broker with respect to any transactions. There are risks that you assume when dealing with other Members (including those who may be
            acting under false pretenses) and all of these risks are borne by you, and not {{ platform }}. We encourage you to use the various
            functionalities of the Platform (e.g., our Item commenting system, and our direct messaging platform) to help evaluate the Member with
            whom you are dealing.
          </li>

          <li>
            <u>No Endorsements of Other Members.</u> {{ platform }} does not control the behavior of Members of the Service or the information or
            content provided by other Members. As a result, {{ platform }} does not guarantee or endorse the authenticity, quality, safety, or
            legality of any Items offered or sold, the truth or accuracy of any listings, or the ability of Sellers to sell Items or of Buyers to buy
            Items. We cannot assure that all transactions will be completed. Additionally, {{ platform }} does not guarantee the ability or intent of
            Members to fulfill their obligations in any transactions. {{ platform }} reserves the right to delay the completion of any transaction for
            a reasonable period of time for purposes of fraud detection and otherwise protecting {{ platform }} and its Members from illegal or
            wrongful activities or other violations of this TOS.
            <p>
              FOR EVERY TRANSACTION THAT IS COMMENCED THROUGH THE PLATFORM, YOU MUST MAKE YOUR OWN INDEPENDENT DETERMINATION REGARDING THE STATEMENTS,
              ITEM DESCRIPTIONS OR THE SELLER’S REPRESENTATIONS AND THE BUYER’S ABILITY TO PAY FOR OR DELIVER THE ITEM(S) OFFERED. PLEASE USE CAUTION,
              COMMON SENSE, AND PRACTICE SAFE TRADING WHEN DEALING WITH OTHER USERS OR OTHERWISE USING THE SERVICE.
            </p>
          </li>
          <li>
            <u>Prohibited Items.</u> {{ platform }} prohibits the listing or sale of any Item that is illegal to sell under any applicable law,
            statute, ordinance, or regulation.
          </li>

          <li>
            <u>Sellers.</u> You must have the right to sell the Items that you make available for sale through the Platform. You must describe your
            Item (including the original price) and all terms of sale in your listing truthfully, accurately and completely.
          </li>

          <li>
            <u>Buyers.</u> You are responsible for reading the full Item listing before making an offer. When you make an offer and your offer is
            accepted by the Seller, you have a contract with the Seller.
          </li>

          <li>
            <u>Electronic Signatures.</u> While {{ platform }} offers optional electronic signature services through the Service, it does not control
            Members’ acceptance of electronic signatures and each Member will make its own determination as to whether it will accept electronic
            signatures for Transactions. In addition and without limiting Section C, the electronic signature services offered through the Services
            are provided “AS-IS” without any warranty of any kind, and {{ platform }} makes no warranties that any electronic signatures generated
            through the electronic services will be effective, enforceable, or reliable. In addition, {{ platform }} disclaims any and all liability
            resulting from your use of, or reliance upon, the electronic signatures services offered through the Services.
          </li>
        </ol>
      </li>
      <li>
        <strong>CAUTIONS FOR GLOBAL USE AND EXPORT AND IMPORT COMPLIANCE</strong>

        <p>
          Due to the global nature of the internet, through the use of {{ platform }}’s network you hereby agree to comply with all local rules
          relating to online conduct and that which is considered acceptable content. Uploading, posting and/or transferring of software, technology
          and other technical data may be subject to the export and import laws of the United States and possibly other countries. Through the use of
          {{ platform }}’s network, you thus agree to comply with all applicable export and import laws, statutes and regulations, including, but not
          limited to, the Export Administration Regulations (<a href="http://www.access.gpo.gov/bis/ear/ear_data.html">
            http://www.access.gpo.gov/bis/ear/ear_data.html </a
          >), as well as the sanctions control program of the United States (<a
            href="http://www.treasury.gov/resource-center/sanctions/Programs/Pages/Programs.aspx"
          >
            http://www.treasury.gov/resource-center/sanctions/Programs/Pages/Programs.aspx </a
          >). You represent and warrant that you:
        </p>
        <ol style="list-style-type: upper-latin">
          <li>
            are not on the list of prohibited individuals which may be identified on any government export exclusion report (<a
              href="http://www.bis.doc.gov/complianceandenforcement/liststocheck.htm"
            >
              http://www.bis.doc.gov/complianceandenforcement/liststocheck.htm </a
            >) nor a member of any other government which may be part of an export-prohibited country identified in applicable export and import laws
            and regulations;
          </li>
          <li>
            agree not to transfer any software, technology or any other technical data through the use of the Services orPlatformto any
            export-prohibited country;
          </li>
          <li>
            agree not to use the Services for any military, nuclear, missile, chemical or biological weaponry end uses that would be a violation of
            the U.S. export laws; and
          </li>
          <li>
            agree not to post, transfer nor upload any software, technology or any other technical data which would be in violation of the U.S. or
            other applicable export and/or import laws.
          </li>
        </ol>
      </li>
      <li>
        <strong>CONTENT PLACED OR MADE AVAILABLE FOR COMPANY SERVICES</strong>

        <ol style="list-style-type: upper-latin">
          <li>
            <u>Rights to Your Content.</u> {{ platform }} shall not lay claim to ownership of any content submitted by any visitor, Member, or user,
            nor make such content available for inclusion on our website. Therefore, you hereby grant and allow for {{ platform }} the below listed
            worldwide, royalty-free and non-exclusive licenses, as applicable:

            <ol style="list-style-type: lower-latin">
              <li>
                The content submitted or made available for inclusion on the publicly accessible areas of {{ platform }}’s sites, the license provided
                to permit to use, distribute, reproduce, modify, adapt, publicly perform and/or publicly display said content on the Services is for
                the sole purpose of providing and promoting the specific area to which this content was placed and/or made available for viewing. This
                license shall be available so long as you are a Member, and shall terminate at such time when you elect to discontinue your
                membership.
              </li>
              <li>
                Photos, audio, video and/or graphics submitted or made available for inclusion on the publicly accessible areas of {{ platform }}'s
                sites, the license provided to permit to use, distribute, reproduce, modify, adapt, publicly perform and/or publicly display said
                content on the Services are for the sole purpose of providing and promoting the specific area in which this content was placed and/or
                made available for viewing. This license shall be available so long as you are a Member of {{ platform }}'s sites and shall terminate
                at such time when you elect to discontinue your membership.
              </li>
              <li>
                For any other content submitted or made available for inclusion on the publicly accessible areas of {{ platform }}'s sites, the
                continuous, binding and completely sub-licensable license which is meant to permit to use, distribute, reproduce, modify, adapt,
                publish, translate, publicly perform and/or publicly display said content, whether in whole or in part, and the incorporation of any
                such content into other works in any arrangement or medium current used or later developed.
              </li>
            </ol>
          </li>
          <li>
            <u>Publicly Accessible Content.</u> Those areas which may be deemed "publicly accessible" areas of {{ platform }}'s sites are those such
            areas of our network properties which are meant to be available to the general public, and which would include message boards and groups
            that are openly available to both users and Members. However, those areas which are not open to the public, and thus available to Members
            only, would include our mail system and instant messaging.
          </li>
        </ol>
      </li>
      <li>
        <strong>CONTRIBUTIONS TO COMPANY WEBSITE</strong>

        <p>
          {{ platform }} provides an area for users and Members to contribute feedback to our website. When you submit ideas, documents, suggestions
          and/or proposals ("<strong>Contributions</strong>") to the Site, you acknowledge and agree that:
        </p>

        <ol style="list-style-type: upper-latin">
          <li>your Contributions do not contain any type of confidential or proprietary information;</li>
          <li>
            {{ platform }} shall not be liable or under any obligation to ensure or maintain confidentiality, expressed or implied, related to any
            Contributions;
          </li>
          <li>{{ platform }} shall be entitled to make use of and/or disclose any such Contributions in any such manner as they may see fit;</li>
          <li>the contributor's Contributions shall automatically become the sole property of {{ platform }}; and</li>
          <li>{{ platform }} is under no obligation to either compensate or provide any form of reimbursement in any manner or nature.</li>
        </ol>
      </li>
      <li>
        <strong>USE AND STORAGE GENERAL PRACTICES</strong>

        <p>
          You herein acknowledge that {{ platform }} may set up any such practices and/or limits regarding the use of thePlatformand Services, without
          limitation of the maximum number of days that any email, message posting or any other uploaded content shall be retained by {{ platform }},
          nor the maximum number of email messages that may be sent and/or received by Member, the maximum volume or size of any email message that
          may be sent from or may be received by Member Account, the maximum disk space allowable that shall be allocated on {{ platform }}'s servers
          on the Member's behalf, and the maximum number of times and duration that Member may access thePlatform andServices in a given period of
          time. In addition, you also agree that {{ platform }} has absolutely no responsibility or liability for the removal or failure to maintain
          storage of any messages and/or other communications or content maintained or transmitted by the Services or through thePlatform. You also
          herein acknowledge that {{ platform }} reserves the right to delete or remove any Member Account that is no longer active for an extended
          period of time. {{ platform }} shall reserve the right to modify, alter and/or update these general practices and limits at our discretion.
          Any messenger service, which may include any web-based versions, shall allow you and the individuals with whom you communicate with the
          ability to save your conversations in your Member Account located on {{ platform }}'s servers. In this manner, you will be able to access
          and search your message history from any computer with internet access. You also acknowledge that others have the option to use and save
          conversations with you in their own personal account on
          <a :href="`http://www.${platformDomain}.com/`"> {{ `http://www.${platformDomain}.com/` }} </a>. It is your agreement to this TOS which
          establishes your consent to allow {{ platform }} to store any and all communications on its servers.
        </p>
        {{ `support@${platformDomain}.com` }}
      </li>
      <li>
        <strong>TERMINATION</strong>

        <ol style="list-style-type: upper-latin">
          <li>
            <u>Termination Rights.</u> As a Member, you may cancel or terminate your Member Account, associated email address and/or access to
            thePlatformor Services by submitting a cancellation or termination request to {{ `support@${platformDomain}.com` }}. As a Member, you
            agree that {{ platform }} may, without any prior written notice, immediately suspend, terminate, discontinue and/or limit your Member
            Account, any email associated with your Member Account, and access to thePlatformor any of the Services. The cause for such termination,
            discontinuance, suspension and/or limitation of access shall include, but is not limited to:

            <ol style="list-style-type: lower-latin">
              <li>any breach or violation of this TOS or any other incorporated agreement, regulation and/or guideline;</li>
              <li>by way of requests from law enforcement or any other governmental agencies;</li>
              <li>the discontinuance, alteration and/or material modification to the Platform or Services, or any part thereof;</li>
              <li>unexpected technical or security issues and/or problems;</li>
              <li>any extended periods of inactivity;</li>
              <li>any engagement by you in any fraudulent or illegal activities; and/or</li>
              <li>the nonpayment of any associated fees that may be owed by you in connection with your Member Account.</li>
            </ol>
          </li>
          <li>
            <u>Obligations of Termination.</u> You herein agree that any and all terminations, suspensions, discontinuances, and or limitations of
            access for cause shall be made at our sole discretion and that we shall not be liable to you or any other third party with regards to the
            termination of your Member Account, associated email address and/or access to thePlatform orany of the Services. The termination of your
            Member Account with shall include any and/or all of the following:
            <ol style="list-style-type: lower-latin">
              <li>the removal of any access to all or part of the Platform or Services offered;</li>
              <li>
                the deletion of your password and any and all related information, files, and any such content that may be associated with or inside
                your Member Account, or any part thereof; and
              </li>
              <li>the barring of any further use of all or part of the Platform or our Services.</li>
            </ol>
          </li>
        </ol>
      </li>
      <li>
        <strong>ADVERTISERS</strong>

        <p>
          Any correspondence or business dealings with, or the participation in any promotions of, advertisers located on or throughPlatform or as
          part of theServices, which may include the payment and/or delivery of such related goods and Services, and any such other term, condition,
          warranty or representation associated with such dealings, are and shall be solely between you and any such advertiser. Moreover, you herein
          agree that {{ platform }} shall not be held responsible or liable for any loss or damage of any nature or manner incurred as a direct result
          of any such dealings or as a result of the presence of such advertisers on the Site.
        </p>
      </li>
      <li>
        <strong>LINKS</strong>

        <p>
          Either {{ platform }} or any third parties may provide links to other websites and/or resources. Thus, you acknowledge and agree that we are
          not responsible for the availability of any such external sites or resources, and as such, we do not endorse nor are we responsible or
          liable for any content, Items, products, advertising or any other materials, on or available from such third party sites or resources.
          Furthermore, you acknowledge and agree that {{ platform }} shall not be responsible or liable, directly or indirectly, for any such damage
          or loss which may be a result of, caused or allegedly to be caused by or in connection with the use of or the reliance on any such content,
          goods or Services made available on or through any such site or resource.
        </p>
      </li>
      <li>
        <strong>INTELLECTUAL PROPERTY AND PROPRIETARY RIGHTS</strong>
        <ol style="list-style-type: upper-latin">
          <li>
            <u>Platform Software.</u> You hereby acknowledge and agree that the Platform and anydatabases, software, hardware and other technologythat
            may be used in connection with thePlatformor Services ("<strong>Software</strong>") shall contain proprietary and confidential material
            that is protected by applicable intellectual property rights and other laws. Furthermore, you herein acknowledge and agree that any
            content which may be contained in any advertisements or information presented by and through thePlatformor Services or by advertisers is
            protected by copyrights, trademarks, patents or other proprietary rights and laws. Therefore, except for that which is expressly permitted
            by applicable law or as authorized by {{ platform }} or such applicable licensor, you agree not to alter, modify, lease, rent, loan, sell,
            distribute, transmit, broadcast, publicly perform or create any plagiaristic works which are based on thePlatformor Services (e.g. content
            or Software), in whole or part. {{ platform }} herein has granted you personal, non-transferable and non-exclusive rights and/or license
            to make use of the object code or our Software on a single computer, as long as you do not, and shall not, allow any third party to
            duplicate, alter, modify, create or plagiarize work from, reverse engineer, reverse assemble or otherwise make an attempt to locate or
            discern any source code, sell, assign, sublicense, grant a security interest in and/or otherwise transfer any such right in the Software.
            Furthermore, you do herein agree not to alter or change the Software in any manner, nature or form, and as such, not to use any modified
            versions of the Software, including and without limitation, for the purpose of obtaining unauthorized access to thePlatformor Services.
            Lastly, you also agree not to access or attempt to access thePlatform orServices through any means other than through the interface which
            is provided by {{ platform }} for use in accessing thePlatformor Services.
          </li>

          <li>
            <u>Ownership.</u> You acknowledge and agree that the Platform and the Services provided, are the sole property of {{ platform }} and
            {{ platform }} retains all right, title and interest, including, without limitation, all intellectual property rights, in and to the
            Platform, Services, Software and any additions, improvements, updates and modifications thereto. You receive no ownership interest in or
            to the Platform, Services, or Software and you are not granted any right or license to use the Software itself, apart from your ability to
            access the Platform, content, and Services under this TOS.
          </li>

          <li>
            <u>Commercial Reuse of Services.</u> You agree not to replicate, duplicate, copy, trade, sell, resell nor exploit for any commercial
            reason any part, use of, or access to {{ platform }}'s Sites.
          </li>
        </ol>
      </li>
      <li>
        <strong>REPRESENTATION AND WARRANTIES; WARRANTY DISCLAIMER</strong>

        <ol style="list-style-type: upper-latin">
          <li>
            <u>Mutual Warranties.</u> Each party hereby represents and warrants to the other party that: i) it has the legal right and authority to
            enter into this TOS; ii) this TOS forms a binding legal obligation on behalf of such party; and iii) it has the legal right and authority
            to perform its obligations under this TOS and to grant the rights and licenses described in this TOS.
          </li>
          <li>
            <u>Compliance with Laws.</u> You acknowledge that the Platform is a general purpose online service and is not specifically designed to
            facilitate compliance with any specific law. You acknowledge that you will access and use the Platform in compliance with all laws
            applicable to you, your Items, your content, and any other content you may access through the Platform. {{ platform }} is not responsible
            for notifying you of any such law, enabling your compliance with any such law, or for your failure to comply. You represent and warrant to
            {{ platform }} that your Items, your content, your use of the Platform and Services, and your use of and access to the Platform, will
            comply with all applicable laws and will not cause {{ platform }} itself or any other Member to violate any applicable laws. By accessing
            and using the Platform, you represent and warrant that you have never been the subject of a complaint, restraining order or any other
            legal action including, without limitation, arrested for, charged with, or convicted of any felony, any criminal offense involving
            violence, abuse, neglect, fraud or larceny, or any offense that involves endangering the safety of others, dishonesty, negligence or the
            illegal distribution of drugs.
          </li>
          <li>
            <u>WARRANTY DISCLAIMER.</u> YOU HEREIN EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
            <ol style="list-style-type: lower-latin">
              <li>
                THE USE OF THE PLATFORM, SERVICES AND SOFTWARE ARE AT THE SOLE RISK BY YOU. THE PLATFORM, SERVICES AND SOFTWARE SHALL BE PROVIDED ON
                AN "AS IS" AND/OR "AS AVAILABLE" BASIS. {{ platform.toUpperCase() }} AND OUR SUBSIDIARIES, AFFILIATES, OFFICERS, EMPLOYEES, AGENTS,
                PARTNERS AND LICENSORS EXPRESSLY DISCLAIM ANY AND ALL WARRANTIES OF ANY KIND WHETHER EXPRESSED OR IMPLIED, INCLUDING, BUT NOT LIMITED
                TO ANY CLAIMS THAT AS TO THE EFFECTIVENESS OR ENFORCEABILITY OF ANY ELECTRONIC SIGNATURES OBTAINED THROUGH THE ELECTRONIC SIGNATURE
                SERVICES AND ANY IMPLIED WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON- INFRINGEMENT.
              </li>
              <li>
                {{ platform }} AND OUR SUBSIDIARIES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS AND LICENSORS MAKE NO SUCH WARRANTIES THAT i) THE PLATFORM,
                SERVICES OR SOFTWARE WILL MEET YOUR REQUIREMENTS; ii) THE PLATFORM, SERVICES OR SOFTWARE SHALL BE UNINTERRUPTED, TIMELY, SECURE OR
                ERROR-FREE; iii) THAT SUCH RESULTS WHICH MAY BE OBTAINED FROM THE USE OF THE PLATFORM, SERVICES OR SOFTWARE WILL BE ACCURATE OR
                RELIABLE; iv) QUALITY OF ANY ITEMS, THE PLATFORM, SERVICES, ANY INFORMATION OR OTHER MATERIAL WHICH MAY BE PURCHASED OR OBTAINED BY
                YOU THROUGH THE PLATFORM, SERVICES OR SOFTWARE WILL MEET YOUR EXPECTATIONS; AND v) THAT ANY SUCH ERRORS CONTAINED IN THE SOFTWARE
                SHALL BE CORRECTED.
              </li>
              <li>
                ANY INFORMATION OR MATERIAL DOWNLOADED OR OTHERWISE OBTAINED BY WAY OF THE PLATFORM, SERVICES OR SOFTWARE SHALL BE ACCESSED BY YOUR
                SOLE DISCRETION AND SOLE RISK, AND AS SUCH YOU SHALL BE SOLELY RESPONSIBLE FOR AND HEREBY WAIVE ANY AND ALL CLAIMS AND CAUSES OF
                ACTION WITH RESPECT TO ANY DAMAGE TO YOUR COMPUTER AND/OR INTERNET ACCESS, DOWNLOADING AND/OR DISPLAYING, OR FOR ANY LOSS OF DATA THAT
                COULD RESULT FROM THE DOWNLOAD OF ANY SUCH INFORMATION OR MATERIAL.
              </li>
              <li>
                NO ADVICE AND/OR INFORMATION, DESPITE WHETHER WRITTEN OR ORAL, THAT MAY BE OBTAINED BY YOU FROM {{ platform.toUpperCase() }} OR BY WAY
                OF OR FROM THE PLATFORM, SERVICES OR SOFTWARE SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED IN THE TOS.
              </li>
            </ol>
          </li>
        </ol>
      </li>
      <li>
        <strong>INDEMNITY</strong>
        <p>
          You hereby agree to indemnify, defend, and hold harmless {{ platform }} and its officers, directors, shareholders, subsidiaries, affiliates,
          employees, agents, contractors, assigns, users, customers, providers, licensees, and successors in interest (“<strong
            >Indemnified Parties</strong
          >“) from any and all claims, losses, liabilities, damages, fees, expenses and costs (including attorneys’ fees, court costs, damage awards,
          and settlement amounts) that result from any claim or allegation (“<strong>Claim</strong>”) against any Indemnified Party arising in any
          manner from: i) your access to or use of the Platform, content, or Services; ii) your unauthorized use of the {{ platform }} Software; iii)
          your user content or any access to or use thereof; iv) your Items; v) any access to or use of your user content or your Items by any Member;
          vi) your collection, use, and disclosure of any user content, vii) your violation of applicable laws; and viii) your breach of any
          representation, warranty, or other provision of this TOS. {{ platform }} will use reasonable efforts to provide you with notice of any such
          claim or allegation, and {{ platform }} will have the right to participate in the defense of any such claim at its expense.
        </p>
      </li>
      <li>
        <strong>LIMITATION OF LIABILITY</strong>

        <ol style="list-style-type: upper-latin">
          <li>
            <u>WAIVER OF LIABILITY.</u> YOU EXPLICITLY ACKNOWLEDGE, UNDERSTAND AND AGREE THAT {{ platform.toUpperCase() }} AND OUR SUBSIDIARIES,
            AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS AND LICENSORS SHALL NOT BE LIABLE TO YOU FOR ANY PUNITIVE, INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES, INCLUDING, BUT NOT LIMITED TO, DAMAGES WHICH MAY BE RELATED TO THE LOSS OF ANY ITEMS,
            PROFITS, GOODWILL, USE, DATA AND/OR OTHER INTANGIBLE LOSSES, EVEN THOUGH WE MAY HAVE BEEN ADVISED OF SUCH POSSIBILITY THAT SAID DAMAGES
            MAY OCCUR, AND RESULT FROM:
          </li>
          <ol style="list-style-type: lower-latin">
            <li>THE USE OR INABILITY TO USE THE PLATFORM OR SERVICE;</li>
            <li>THE COST OF PROCURING SUBSTITUTE ITEMS AND SERVICES;</li>
            <li>UNAUTHORIZED ACCESS TO OR THE ALTERATION OF YOUR TRANSMISSIONS AND/OR DATA;</li>
            <li>STATEMENTS OR CONDUCT OF ANY SUCH THIRD PARTY ON THE PLATFORM;</li>
            <li>AND ANY OTHER MATTER WHICH MAY BE RELATED TO THE PLATFORM OR SERVICE.</li>
          </ol>
          <li>
            <u>MAXIMUM LIABILITY.</u> {{ platform }}’S TOTAL CUMULATIVE LIABILITY IN CONNECTION WITH THIS TOS AND ALL CONTENT AND SERVICES PROVIDED
            UNDER THIS TOS OR THROUGH THE PLATFORM, WHETHER IN CONTRACT OR TORT OR OTHERWISE, WILL NOT EXCEED $500 OR THE GREATER OF $500 OR THE
            AMOUNTS THAT YOU HAVE ACCEPTED OR PURCHASED THROUGH THE PLATFORM WITHIN LAST 12 MONTHS. YOU AGREE THAT {{ platform }} WOULD NOT ENTER INTO
            THIS TOS WITHOUT THESE LIMITATIONS ON ITS LIABILITY.
          </li>
          <li>
            <u>EXCLUSION AND LIMITATIONS.</u> THERE ARE SOME JURISDICTIONS WHICH DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR THE LIMITATION OF
            EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES. THEREFORE, SOME OF THE ABOVE LIMITATIONS OF SECTIONS WARRANTY DISCLAIMERS
            AND LIMITATION OF LIABILITY MAY NOT APPLY TO YOU.
          </li>
        </ol>
      </li>
      <li>
        <strong>RELEASE</strong>

        <p>
          In the event you have a dispute, you agree to release {{ platform }} (and its officers, directors, employees, agents, parent subsidiaries,
          affiliates, co-branders, partners and any other third parties) from claims, demands and damages (actual and consequential) of every kind and
          nature, known and unknown, suspected or unsuspected, disclosed and undisclosed, arising out of or in any way connected to such dispute.
        </p>
      </li>
      <li>
        <strong>SPECIAL ADMONITION RELATED TO FINANCIAL MATTERS</strong>

        <p>
          Should you intend to create or to join any service, receive or request any such news, messages, alerts or other information from the
          Services concerning companies, stock quotes, investments or securities, please review the above Sections Warranty Disclaimers and
          Limitations of Liability again. In addition, for this particular type of information, the phrase "Let the investor beware" is appropriate.
          {{ platform }}'s content is provided primarily for informational purposes, and no content that shall be provided or included in the Services
          is intended for trading or investing purposes. {{ platform }} and our licensors shall not be responsible or liable for the accuracy,
          usefulness or availability of any information transmitted and/or made available by way of the Services, and shall not be responsible or
          liable for any trading and/or investment decisions based on any such information.
        </p>
      </li>
      <li>
        <strong>TRADEMARK INFORMATION</strong>

        <p>
          You herein acknowledge, understand and agree that all of the {{ platform }} trademarks, copyright, trade name, service marks, and other
          {{ platform }} logos and any brand features, and/or product and service names are trademarks and as such, are and shall remain the property
          of {{ platform }}. You herein agree not to display and/or use in any manner the {{ platform }} logo or marks without obtaining
          {{ platform }}'s prior written consent.
        </p>
      </li>
      <li>
        <strong>COPYRIGHT OR INTELLECTUAL PROPERTY INFRINGEMENT CLAIMS NOTICE &amp; PROCEDURES</strong>

        <p>
          {{ platform }} will always respect the intellectual property of others, and we ask that you do the same. With regards to appropriate
          circumstances and at its sole discretion, {{ platform }} may disable and/or terminate the Member Accounts of any user who violates our TOS
          and/or infringes the rights of others. If you feel that your work has been duplicated in such a way that would constitute copyright
          infringement, or if you believe your intellectual property rights have been otherwise violated, you should provide to us the following
          information:
        </p>

        <ol style="list-style-type: upper-latin">
          <li>
            The electronic or the physical signature of the individual that is authorized on behalf of the owner of the copyright or other
            intellectual property interest;
          </li>
          <li>A description of the copyrighted work or other intellectual property that you believe has been infringed upon;</li>
          <li>A description of the location of the site which you allege has been infringing upon your work;</li>
          <li>Your physical address, telephone number, and email address;</li>
          <li>
            A statement, in which you state that the alleged and disputed use of your work is not authorized by the copyright owner, its agents or the
            law;
          </li>
          <li>
            And finally, a statement, made under penalty of perjury, that the aforementioned information in your notice is truthful and accurate, and
            that you are the copyright or intellectual property owner, representative or agent authorized to act on the copyright or intellectual
            property owner's behalf.
          </li>
        </ol>

        <p>The {{ platform }} Agent for notice of claims of copyright or other intellectual property infringement can be contacted as follows:</p>
        <p class="mb-0">Mailing Address: {{ platform }}</p>
        <p class="mb-0">3303 N. Mississippi Avenue, Suite 503 Portland, OR 97227</p>
        <p class="mb-0">Attn: Copyright Agent</p>
        <p class="mb-0">Telephone:</p>
        <p>Email: {{ `support@${platformDomain}.com` }}</p>
      </li>
      <li>
        <strong>ARBITRATION</strong>

        <p>
          Except as otherwise provided below, the parties will attempt to resolve all disputes, controversies, or claims arising under, out of, or
          relating to this TOS, including the formation, validity, binding effect, interpretation, performance, breach or termination, of this TOS and
          the arbitrability of the issues submitted to arbitration hereunder and non-contractual claims relating to this TOS (each, a
          “<strong>Dispute</strong>”), in accordance with the procedures set forth in this Section. If any Dispute cannot be resolved through
          negotiations between the parties within 5 days of notice from one party to the other of the Dispute, such Dispute will be finally settled
          through binding arbitration under the arbitration of the American Arbitration Association (“<strong>AAA</strong>”) then in effect (the
          “<strong>Rules</strong>”). Either party may commence the arbitration by delivering a request for arbitration as specified in the Rules. The
          arbitration will be conducted before a sole neutral arbitrator selected by agreement of the parties. If the parties cannot agree on the
          appointment of a single arbitrator within 30 days (the “<strong>Initial Period</strong>”) after either party to this TOS delivers a request
          for arbitration, a neutral arbitrator will be selected as provided in the Rules. The arbitration will be conducted exclusively in the
          English language at a site in Denver, Colorado. The award of the arbitrator will be the exclusive remedy of the parties for all claims,
          counterclaims, issues, or accountings presented or plead to the arbitrator. The award of the arbitrators will require payment of the costs,
          fees, and expenses incurred by the prevailing party in any such arbitration by the non-prevailing party. Judgment upon the award may be
          entered in any court or governmental body having jurisdiction thereof. Any additional costs, fees, or expenses incurred in enforcing the
          award may be charged against the party that resists its enforcement.
        </p>
      </li>
      <li>
        <strong>CHOICE OF LAW AND FORUM</strong>

        <p>
          It is at the mutual agreement of both you and {{ platform }} with regard to the TOS that the relationship between the parties shall be
          governed by the laws of the state of Colorado without regard to its conflict of law provisions and that any and all claims, negotiations,
          arbitrations, causes of action and/or disputes, arising out of or relating to the TOS, or the relationship between you and {{ platform }}.
          Subject to Section 25 (ARBITRATION), each party agrees that it will bring any action or proceeding arising from or relating to this TOS
          exclusively, in the County of Denver, Colorado. You and {{ platform }} agree to submit to the jurisdiction of the courts as previously
          mentioned, and agree to waive any and all objections to the exercise of jurisdiction over the parties by such courts and to venue in such
          courts.
        </p>
      </li>
      <li>
        <strong>GENERAL INFORMATION</strong>
        <ol style="list-style-type: upper-latin">
          <li>
            <u>Notice.</u> {{ platform }} may furnish you with notices, including those with regards to any changes to the TOS, including but not
            limited to email, regular mail, MMS or SMS, text messaging, postings on the Platform, or other reasonable means currently known or any
            which may be hereinafter developed. Any such notices may not be received if you violate any aspects of the TOS by accessing the Platform
            or use the Services in an unauthorized manner. Your acceptance of this TOS constitutes your agreement that you are deemed to have received
            any and all notices that would have been delivered had you accessed the Platform or Services in an authorized manner.
          </li>
          <li>
            <u>Third Party Beneficiaries.</u> You herein acknowledge, understand and agree, unless otherwise expressly provided in this TOS, that
            there shall be no third-party beneficiaries to this TOS.
          </li>
          <li>
            <u>Subcontracting.</u> {{ platform }} may subcontract the performance of any of its obligations and the provision of any of the Services
            under this Agreement to any third party.
          </li>
          <li>
            <u>Audit.</u> At all times during the Term, and for at least three (3) years after termination of this Agreement, you will maintain
            complete and accurate records of all transactions performed through the Service. {{ platform }} may, during normal business hours and upon
            at least 5 business days prior notice, audit such records relating to your transactions performed through the Service in order to verify
            that you have complied with these TOS. You will pay to {{ platform }} any amounts shown by any such audit to be owing, as reasonably
            determined by {{ platform }}, plus interest of the lesser of 1.5% per month or the maximum rate permitted by applicable law, from due date
            until paid. {{ platform }} will pay the cost of such audits unless an audit reveals an under payment of 3% or more, in which case, you
            will reimburse {{ platform }} for the cost of the audit. You will immediately upon written notice from {{ platform }} pay
            {{ platform }} the amount of any overpayment revealed by the audit, together with any reimbursement payable pursuant to the preceding
            sentence.
          </li>
          <li>
            <u>Entire Agreement.</u> This TOS constitutes the entire agreement between you and {{ platform }} and shall govern the use of the Platform
            and Services, superseding any prior version of this TOS between you and us with respect to the Platform and Services. You may also be
            subject to additional terms and conditions that may apply when you use or purchase certain other {{ platform }} Services, affiliate
            services, third-party content or third-party software.
          </li>
          <li>
            <u>Waiver and Severability of Terms.</u> At any time, should {{ platform }} fail to exercise or enforce any right or provision of the TOS,
            such failure shall not constitute a waiver of such right or provision. If any provision of this TOS is found by a court of competent
            jurisdiction to be invalid, the parties nevertheless agree that the court should endeavor to give effect to the parties' intentions as
            reflected in the provision, and the other provisions of the TOS remain in full force and effect.
          </li>
          <li>
            <u>No Right of Survivorship Non-Transferability.</u> You acknowledge, understand and agree that your Member Account is non-transferable
            and any rights to your ID and/or contents within your Member Account shall terminate upon your death. Upon receipt of a copy of a death
            certificate, your Member Account may be terminated and all contents therein permanently deleted.
          </li>
          <li>
            <u>Statute of Limitations.</u> You acknowledge, understand and agree that regardless of any statute or law to the contrary, any claim or
            action arising out of or related to the use of the Platform or Services or the TOS must be filed within 1 year(s) after said claim or
            cause of action arose or shall be forever barred.
          </li>
          <li><u>Violations.</u> Please report any and all violations of this TOS to {{ platform }} as follows:</li>
        </ol>
      </li>
    </ol>
    <p class="mb-0">Mailing Address: {{ platform }}</p>
    <p class="mb-0">3303 N. Mississippi Ave, Ste 503, Portland, OR 97227</p>
    <p class="mb-0">Telephone:</p>
    <p class="mb-0">Email: {{ `support@${platformDomain}.com` }}</p>
  </div>
</template>

<script>
export default {
  components: {},
  props: {},
  data() {
    return {
      industry: APP_INDUSTRY,
    };
  },
  computed: {
    platform() {
      if (this.industry == "cannabis") {
        return "Apex Trading";
      }
      if (this.industry == "hemp") {
        return "Bushel44";
      }
      return "N/A";
    },
    platformDomain() {
      if (this.industry == "cannabis") {
        return "apextrading";
      }
      if (this.industry == "hemp") {
        return "bushel44";
      }
      return "N/A";
    },
  },
  methods: {},
};
</script>

<style scoped></style>
